/**
 * Thin fetch wrapper for the admin API. Every call goes through here so
 * credentials, JSON handling and error shape are consistent across the whole
 * app, and so tests only need to mock one function: `globalThis.fetch`.
 *
 * The admin session is an httpOnly cookie (see spec section 6). We never read
 * or set it from JavaScript; `credentials: 'include'` is enough to carry it.
 */

const API_BASE = '/api/v1';

export type ApiErrorKind = 'network' | 'unauthorized' | 'forbidden' | 'validation' | 'not_found' | 'unknown';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  readonly details: unknown;

  constructor(kind: ApiErrorKind, message: string, status: number | null = null, details: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.kind = kind;
    this.status = status;
    this.details = details;
  }
}

function kindForStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 400 || status === 409 || status === 422) return 'validation';
  return 'unknown';
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(init.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
      ...init,
    });
  } catch (err) {
    throw new ApiError('network', err instanceof Error ? err.message : 'network error');
  }

  if (!res.ok) {
    const body = await parseBody(res);
    const message =
      (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string'
        ? body.message
        : null) ?? `request failed with status ${res.status}`;
    // The server's error shape is always `{ code, message, details }` (see
    // AppError in packages/server/src/errors.ts). `details` is what carries
    // the per-field zod issues on a 400 — surface that, not the whole
    // envelope, so callers can do `err.details.issues` directly instead of
    // reaching through a level that duplicates `message`/`code`.
    const details =
      body && typeof body === 'object' && 'details' in body ? (body as { details: unknown }).details : null;
    throw new ApiError(kindForStatus(res.status), message, res.status, details);
  }

  if (res.status === 204) return undefined as T;
  return (await parseBody(res)) as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, { method: 'POST', body: body !== undefined ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, { method: 'PATCH', body: body !== undefined ? JSON.stringify(body) : undefined }),
  del: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
  postForm: <T>(path: string, form: FormData): Promise<T> => request<T>(path, { method: 'POST', body: form }),
};

export function queryString(params: Record<string, string | undefined | null>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') usp.set(key, value);
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}
